import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, Tag, Spin } from 'antd';
import { Line, Pie } from '@ant-design/plots';
import { resolveApiUrl } from '../api/http';

const BehaviorDashboardPage = () => {
  const [summary, setSummary] = useState(null);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [riskDistribution, setRiskDistribution] = useState([]); // [{risk_level, count}]
  const [trend7d, setTrend7d] = useState([]); // [{date, high_risk}]

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      const authHeader = token ? { Authorization: 'Bearer ' + token } : {};

      const sRes = await fetch(resolveApiUrl('/api/behavior/dashboard/summary'), { credentials:'include', headers: authHeader });
      const sData = await sRes.json();
      setSummary(sData);
      const dRes = await fetch(resolveApiUrl('/api/behavior/decision/overview'), { credentials:'include', headers: authHeader });
      const dData = await dRes.json();
      setDecision(dData);
      // 临时使用最近 7 天事件聚合 & 风险占比：缺少后端专用接口时从 /events 拉取前 500 条作为近似
      const evRes = await fetch(resolveApiUrl('/api/behavior/events?page=1&pageSize=500'), { credentials:'include', headers: authHeader });
      const evData = await evRes.json();
      if(evRes.ok && Array.isArray(evData.list)){
        const distMap = {};
        const trendMap = {};
        evData.list.forEach(evt => {
          // 风险占比
          if(evt.risk_level){ distMap[evt.risk_level] = (distMap[evt.risk_level]||0)+1; }
          // 近7天(根据 event_time 日期) 统计高风险
          if(evt.event_time){
            const d = evt.event_time.slice(0,10); // YYYY-MM-DD
            const isHigh = evt.risk_level && ['高','极高'].includes(evt.risk_level);
            if(isHigh){ trendMap[d] = (trendMap[d]||0)+1; }
          }
        });
        // 生成过去 7 天的日期序列
        const today = new Date();
        const days = [];
        for(let i=6;i>=0;i--){ const dt = new Date(today.getTime()-i*86400000); days.push(dt.toISOString().slice(0,10)); }
        setTrend7d(days.map(d=>({ date:d, high_risk: trendMap[d]||0 })));
        setRiskDistribution(Object.entries(distMap).map(([risk_level,count])=>({ risk_level, count })));
      }
    } catch(e){ console.error(e); }
    setLoading(false);
  };

  useEffect(()=>{ loadData(); }, []);

  const deptCols = [
    { title:'部门', dataIndex:'department' },
    { title:'近30天高风险事件', dataIndex:'high_risk_events' }
  ];
  const tagCols = [
    { title:'标签', dataIndex:'name' },
    { title:'分类', dataIndex:'category' },
    { title:'高风险事件次数', dataIndex:'risk_incidents_count' }
  ];
  const empCols = [
    { title:'员工ID', dataIndex:'employee_id', width:90 },
    { title:'姓名', dataIndex:'name', width:120 },
    { title:'部门', dataIndex:'department', width:160 },
    { title:'高风险事件次数', dataIndex:'high_risk_events', width:160, render:v=> <Tag color={v>10?'red':v>5?'orange':'blue'}>{v}</Tag> }
  ];

  // 图表配置（临时基于前端聚合）
  const lineCfg = {
    data: trend7d,
    xField: 'date',
    yField: 'high_risk',
    height: 200,
    smooth: true,
    point: { size:4 },
    meta: { high_risk:{ alias:'高风险事件'} },
    color: '#ff4d4f'
  };
  const pieCfg = {
    data: riskDistribution.map(r=>({ type:r.risk_level, value:r.count })),
    angleField: 'value',
    colorField: 'type',
    radius: 0.9,
    label: { type:'outer', content:'{name} {percentage}' },
    legend: { position:'bottom' },
  };

  return (
    <div style={{ padding:24 }}>
      <h2 style={{marginBottom:16}}>行为安全仪表板</h2>
      <Row gutter={16} style={{ marginBottom:16 }}>
        <Col span={6}>
          <Card loading={loading} title='标签总数'>
            <div style={{ fontSize:28, fontWeight:'bold' }}>{summary?.tag_count ?? '-'}</div>
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading} title='今日事件'>
            <div style={{ fontSize:28, fontWeight:'bold' }}>{summary?.events_today ?? '-'}</div>
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading} title='近7天高风险事件'>
            <div style={{ fontSize:28, fontWeight:'bold' }}>{summary?.high_risk_events_7d ?? '-'}</div>
          </Card>
        </Col>
        <Col span={6}>
          <Card loading={loading} title='高风险员工(Top)'>
            <div style={{ fontSize:28, fontWeight:'bold' }}>{decision?.high_risk_employees?.length ?? '-'}</div>
          </Card>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom:16 }}>
        <Col span={12}>
          <Card loading={loading} title='近7天高风险事件趋势'>
            <Spin spinning={loading && !trend7d.length}>
              <Line {...lineCfg} />
            </Spin>
          </Card>
        </Col>
        <Col span={12}>
          <Card loading={loading} title='风险等级占比 (近似)'>
            <Spin spinning={loading && !riskDistribution.length}>
              <Pie {...pieCfg} />
            </Spin>
          </Card>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Card loading={loading} title='部门风险对比'>
            <Table size='small' pagination={false} columns={deptCols} dataSource={decision?.department_risk || []} rowKey={(r)=>r.department||Math.random()} />
          </Card>
        </Col>
        <Col span={8}>
          <Card loading={loading} title='标签高风险事件排名'>
            <Table size='small' pagination={false} columns={tagCols} dataSource={decision?.tag_risk_rank || []} rowKey='id' />
          </Card>
        </Col>
        <Col span={8}>
          <Card loading={loading} title='高风险员工榜'>
            <Table size='small' pagination={false} columns={empCols} dataSource={decision?.high_risk_employees || []} rowKey='employee_id' />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BehaviorDashboardPage;
